/* eslint-disable jira/import-whitelist */
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	getDefaultJql as getDefaultJqlPin,
	mapFilterToJql as mapFilterToJqlPin,
} from '@atlassian/jira-spa-apps-project-issue-navigator-utils/src/index.tsx';
import type { RouterDataContext, RouterContext } from '@atlassian/react-resource-router';
import { NIN_PROJECT_OPERATION_SCOPE } from '@atlassian/jira-issue-navigator/src/common/constants.tsx';
import { JiraFilterAri } from '@atlassian/ari/jira/filter';
import { fg } from '@atlassian/jira-feature-gating';
import { createViewAri } from '@atlassian/jira-issue-navigator-view-ari/src/index.tsx';
import type { JiraIssueSearchInput } from '@atlassian/jira-issue-navigator/src/__generated__/main_IssueNavigatorQuery.graphql';
import type { JiraHydrateJqlInput } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import { defaultOrderFields, sortDirections } from '../../constants.tsx';

export const getDefaultJqlGin = () =>
	`ORDER BY ${defaultOrderFields.created} ${sortDirections.DESC}`;

export const getParams = ({ match: { params }, query }: RouterDataContext | RouterContext) => {
	const { projectKey } = params;

	const { filter, jql, selectedIssue, groupBy, hideDone } = query;

	// Default JQL when no relevant query parameter is set
	const defaultJql =
		projectKey !== null && projectKey !== undefined
			? getDefaultJqlPin(toProjectKey(projectKey))
			: getDefaultJqlGin();

	return {
		page: 1,
		jql,
		issueKey: selectedIssue,
		projectKey,
		filter,
		defaultJql,
		groupBy,
		hideDone: hideDone === 'true' || hideDone === 'false' ? hideDone === 'true' : undefined,
	};
};

const getJqlOrFilterIdOrNull = (
	jqlParam?: string,
	filter?: string,
	projectKey?: string | null,
): { jql: string } | { filterId: string } | null => {
	// If JQL param has been defined (even if it's empty) we should use it
	if (typeof jqlParam === 'string') {
		return { jql: jqlParam };
	}
	// If filter is non-empty then use the filter parameter
	if (typeof filter === 'string' && filter !== '') {
		if (isFilterId(filter)) {
			return { filterId: filter };
		}

		const filterJql =
			projectKey !== null && projectKey !== undefined
				? mapFilterToJqlPin(projectKey, filter)
				: undefined;

		if (filterJql !== undefined) {
			return { jql: filterJql };
		}
	}

	return null;
};

export const getJqlOrFilterId = (
	defaultJql: string,
	jqlParam?: string,
	filter?: string,
	projectKey?: string | null,
): { jql: string } | { filterId: string } => {
	// Fallback to the default JQL if neither JQL or valid filter parameters have been provided.
	return getJqlOrFilterIdOrNull(jqlParam, filter, projectKey) ?? { jql: defaultJql };
};

export const getIssueSearchInput = (
	defaultJql: string,
	jqlParam?: string,
	filter?: string,
	projectKey?: string | null,
): Pick<JiraIssueSearchInput, 'jql' | 'filterId' | 'searchWithLastUsedJql'> => {
	// When in project scope fallback to searching by last used JQL, otherwise fallback to default JQL.
	const fallback = projectKey ? { searchWithLastUsedJql: true } : { jql: defaultJql };

	// Fallback if neither JQL or valid filter parameters have been provided.
	return getJqlOrFilterIdOrNull(jqlParam, filter, projectKey) ?? fallback;
};

export const getHydrateJqlInput = ({
	activationId,
	cloudId,
	defaultJql,
	jqlParam,
	filter,
	projectKey,
	itemId,
}: {
	activationId: string;
	cloudId: string;
	defaultJql: string;
	jqlParam?: string;
	filter?: string;
	projectKey?: string | null;
	itemId?: string; // Remove optional when cleaning up saved_list_view_technical_changes
}): JiraHydrateJqlInput => {
	let fallback;
	if (fg('saved_list_view_technical_changes')) {
		fallback = projectKey
			? {
					viewQueryInput: {
						projectKeyAndItemIdQuery: {
							cloudId,
							projectKey,
							itemId: itemId ?? 'list',
						},
					},
				}
			: {
					viewQueryInput: {
						viewAri: createViewAri({
							activationId,
							cloudId,
						}),
					},
				};
	} else {
		// When in project scope fallback to searching by last used JQL, otherwise fallback to default JQL.
		fallback = projectKey
			? {
					lastUsedJqlForIssueNavigator: {
						list: {
							operationScope: NIN_PROJECT_OPERATION_SCOPE,
							projectKey,
						},
					},
				}
			: { jql: defaultJql };
	}

	// Fallback if neither JQL or valid filter parameters have been provided.
	const result = getJqlOrFilterIdOrNull(jqlParam, filter, projectKey) ?? fallback;

	// Hydration API expects filterId in ARI format
	if ('filterId' in result) {
		return {
			filterId: JiraFilterAri.create({
				siteId: cloudId,
				filterId: result.filterId,
				activationId,
			}).toString(),
		};
	}

	return result;
};

/** Return `true` if the route is using a chromeless layout, meaning no navigation is visible. */
export const isChromelessRoute = ({ route }: RouterContext): boolean => {
	return route.layout?.isChromeless ?? false;
};
